import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { arrayOf, bool, func, shape, string } from 'prop-types';
import {
  Box,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { connect } from 'react-redux';
import { EvaluateFlag } from 'common/FeatureFlags/Flag';
import SelectProducts from '../../../create-new-common/SelectProducts';
import { mapDispatchToProps, mapState } from './Select.connector';

const renderTooltipCell = ({ value }) => (
  <Tooltip arrow placement="bottom" title={value || ''}>
    <span
      style={{
        display: 'inline-block',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        verticalAlign: 'bottom',
        whiteSpace: 'nowrap',
      }}
    >
      {value}
    </span>
  </Tooltip>
);

const SelectLocationStep = ({
  allEnabled,
  authorizationBy,
  setBatchEnabledOptions,
  enabledOptions,
  header,
  inactiveListOptions,
  quickFilterInput,
  setEnabledOptions,
  setHasChangesToStepTwo,
  setSelectStepSearchTerm,
  toggleAllMessage,
}) => {
  const initialRender = useRef(true);
  const [quickFilterValues, setQuickFilterValues] = useState([]);
  const isCreateCarrierAuthEnabled = EvaluateFlag(
    process.env.REACT_APP_LD_CREATE_CARRIER_AUTHORIZATIONS
  );
  const isShowingLocations = authorizationBy === 'carrier';

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      setQuickFilterValues(
        quickFilterInput
          .split(' ')
          .map(v => v.trim())
          .filter(Boolean)
      );
    }, 200);
    return () => clearTimeout(debounceTimeout);
  }, [quickFilterInput]);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }
    if (setHasChangesToStepTwo) {
      setHasChangesToStepTwo(true);
    }
  }, [enabledOptions, setHasChangesToStepTwo]);

  const columns = useMemo(
    () =>
      isShowingLocations
        ? [
            {
              field: 'locationDisplay',
              flex: 1,
              headerName: 'Locations',
              minWidth: 180,
              renderCell: renderTooltipCell,
            },
          ]
        : [
            {
              field: 'carrierName',
              headerName: 'Carrier',
              maxWidth: 435,
              minWidth: 160,
              renderCell: renderTooltipCell,
              width: 435,
            },
            {
              align: 'center',
              field: 'scac',
              headerAlign: 'center',
              headerName: 'SCAC',
              maxWidth: 80,
              minWidth: 60,
              width: 80,
            },
            {
              align: 'center',
              field: 'fein',
              headerAlign: 'center',
              headerName: 'FEIN',
              maxWidth: 130,
              minWidth: 110,
              width: 130,
            },
          ],
    [isShowingLocations]
  );

  const rows = useMemo(
    () =>
      inactiveListOptions.map((item, index) => {
        if (isShowingLocations) {
          return {
            id: item.value || `location-row-${index}`,
            locationDisplay: item.label || '--',
          };
        }
        const fein = item.fein || '';
        const feinFormatted =
          fein && fein.length === 9 ? `${fein.slice(0, 2)}-${fein.slice(2)}` : fein;
        return {
          carrierName: item.label || '--',
          fein: feinFormatted || '--',
          id: item.value || `carrier-row-${index}`,
          scac: item.scac || '--',
        };
      }),
    [inactiveListOptions, isShowingLocations]
  );

  const filterModel = useMemo(() => ({ items: [], quickFilterValues }), [quickFilterValues]);

  const handleSelection = useCallback(
    rowSelectionModel => {
      const selectedIds = new Set(rowSelectionModel.map(id => String(id)));
      const updates = inactiveListOptions.reduce((acc, option) => {
        const isCurrentlySelected = !!option.selected;
        const willBeSelected = selectedIds.has(String(option.value));
        if (isCurrentlySelected !== willBeSelected) {
          acc.push({ id: option.value, selected: willBeSelected });
        }
        return acc;
      }, []);

      if (updates.length > 0) {
        setBatchEnabledOptions(updates);
      }
    },
    [inactiveListOptions, setBatchEnabledOptions]
  );

  const selectedRowIds = useMemo(
    () => enabledOptions?.map(opt => opt.value) || [],
    [enabledOptions]
  );

  const handleClearSearch = () => {
    setSelectStepSearchTerm('');
  };

  if (!isCreateCarrierAuthEnabled) {
    return (
      <SelectProducts
        allEnabled={allEnabled}
        enabledOptions={enabledOptions}
        header={header}
        inactiveListOptions={inactiveListOptions}
        setEnabledOptions={setEnabledOptions}
        setHasChangesToStepTwo={setHasChangesToStepTwo}
        toggleAllMessage={toggleAllMessage}
      />
    );
  }

  return (
    <>
      <Divider />
      <Typography fontWeight={700} sx={{ mb: 2, mt: 3, px: 3 }} variant="body1">
        Select {isShowingLocations ? 'Locations' : 'Carriers'}
      </Typography>
      <Box display="flex" flexDirection="column" minHeight={0} px={3} sx={{ flex: 1 }}>
        <Box pb={2}>
          <TextField
            fullWidth
            label="Search"
            onChange={e => setSelectStepSearchTerm(e.target.value)}
            placeholder={
              isShowingLocations ? 'Search Locations' : 'Search Carrier Name, SCAC or FEIN'
            }
            size="small"
            slotProps={{
              input: {
                endAdornment: quickFilterInput ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="clear search"
                      edge="end"
                      onClick={handleClearSearch}
                      size="small"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& input': {
                  padding: '14px 8px',
                },
              },
            }}
            value={quickFilterInput}
            variant="outlined"
          />
        </Box>

        <Box sx={{ flex: 1, minHeight: 200, overflow: 'hidden' }}>
          <DataGridPremium
            checkboxSelection
            columns={columns}
            disableColumnFilter
            disableColumnMenu
            disableColumnSelector
            disableDensitySelector
            filterModel={filterModel}
            hideFooterRowCount
            onRowSelectionModelChange={handleSelection}
            rows={rows}
            rowSelectionModel={selectedRowIds}
            sortingOrder={['asc', 'desc', null]}
            sx={{
              '& .MuiDataGrid-main': { overflowX: 'hidden' },
              '& .MuiDataGrid-virtualScroller': {
                overflowX: 'hidden',
                overflowY: rows.length > 0 ? 'auto' : 'hidden',
              },
            }}
          />
        </Box>
      </Box>
    </>
  );
};

SelectLocationStep.propTypes = {
  allEnabled: bool,
  authorizationBy: string.isRequired,
  enabledOptions: arrayOf(shape({})),
  header: string,
  inactiveListOptions: arrayOf(shape({})).isRequired,
  quickFilterInput: string,
  setBatchEnabledOptions: func.isRequired,
  setEnabledOptions: func.isRequired,
  setHasChangesToStepTwo: func,
  setSelectStepSearchTerm: func.isRequired,
  toggleAllMessage: string,
};

SelectLocationStep.defaultProps = {
  allEnabled: false,
  enabledOptions: [],
  header: '',
  quickFilterInput: '',
  setHasChangesToStepTwo: undefined,
  toggleAllMessage: '',
};

export default connect(mapState, mapDispatchToProps)(SelectLocationStep);
